import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { reviews } from './review-data';

export default function ReviewsPage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Product Reviews',
    description: 'Real homes, real results. Every product tested and reviewed by our team',
    url: `${siteUrl}/reviews`,
    numberOfItems: reviews.length,
    itemListElement: reviews.map((review, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Review',
        itemReviewed: {
          '@type': 'Product',
          name: review.title,
          image: review.image,
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: review.rating,
          bestRating: 5,
        },
        author: {
          '@type': 'Person',
          name: review.author,
        },
        datePublished: review.date,
        reviewBody: review.reviewText,
      },
    })),
  };

  const categories = ['All', 'Living Room Furniture', 'Bedroom Furniture', 'Bedroom Storage', 'Bedroom Bedding', 'Outdoor / Patio Furniture', 'Bathroom Storage', 'Bathroom Lighting', 'Bathroom Accessories', 'Bathroom Mirrors', 'Bathroom', 'Kitchenware', 'Kitchen Decor', 'Wall Art', 'Entryway Storage', 'Entryway', 'Dining Room Storage', 'Dining Room Furniture', 'Home Office Storage'];

  const filteredReviews = selectedCategory === 'All' 
    ? reviews 
    : reviews.filter(review => review.category === selectedCategory);

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    const formData = new URLSearchParams();
    formData.append('email', email);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5dbhf1dn6dhfpiac24g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Product Reviews - Interior Decor Solutions | Honest Home Decor Reviews"
        description="Real homes, real results. Every product tested and reviewed by our team. Find honest reviews of bathroom storage, lighting, accessories, and more home decor products."
        keywords="product reviews, home decor reviews, bathroom storage reviews, bathroom lighting reviews, bathroom accessories, honest product reviews, verified purchases"
        canonical={`${siteUrl}/reviews`}
        jsonLd={jsonLd}
      />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="px-4 md:px-8 lg:px-20 h-[70px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <h1 className="text-[#1A4D4D] text-xl md:text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}>
              Interior Decor Solutions
            </h1>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Home</Link>
            <Link to="/blog" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Blog</Link>
            <Link to="/room-inspiration" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Room Inspiration</Link>
            <Link to="/reviews" className="text-[#FF6B6B] font-medium text-base whitespace-nowrap">Reviews</Link>
            <Link to="/about" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">About</Link>
          </div>

          <button className="hidden lg:flex bg-[#1A4D4D] text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-[#0F3333] transition-all whitespace-nowrap items-center gap-2">
            <i className="ri-mail-line"></i>
            Get Ideas
          </button>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-[#1A4D4D]"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block text-[#1A4D4D] font-medium text-base py-2">Home</Link>
              <Link to="/blog" className="block text-[#1A4D4D] font-medium text-base py-2">Blog</Link>
              <Link to="/room-inspiration" className="block text-[#1A4D4D] font-medium text-base py-2">Room Inspiration</Link>
              <Link to="/reviews" className="block text-[#FF6B6B] font-medium text-base py-2">Reviews</Link>
              <Link to="/about" className="block text-[#1A4D4D] font-medium text-base py-2">About</Link>
              <button className="w-full bg-[#1A4D4D] text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-[#0F3333] transition-all whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-mail-line"></i>
                Get Ideas
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-20 px-4 md:px-8 lg:px-20 mt-[70px] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/ba31796eded255e570418717fff56e0a.jpg"
            alt="Product Reviews Background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <h1 className="text-white text-4xl md:text-6xl lg:text-7xl font-bold mb-4 md:mb-6 drop-shadow-lg">Product Reviews</h1>
          <p className="text-white font-bold text-base md:text-xl max-w-2xl mx-auto leading-relaxed drop-shadow-md">
            Real homes, real results. Every product tested and reviewed by our team
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 md:px-8 lg:px-20 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-4">
            <span className="text-[#1A4D4D] font-semibold text-sm">Filter by:</span>
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 md:px-5 py-2 rounded-full font-medium text-sm transition-all whitespace-nowrap cursor-pointer ${
                    selectedCategory === category
                      ? 'bg-[#FF6B6B] text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-20 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          {filteredReviews.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredReviews.map((review) => (
                <Link 
                  key={review.id}
                  to={`/reviews/${review.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-xl transition-all hover:border-[#FF6B6B]"
                >
                  {/* Review Image */}
                  <div className="w-full h-[250px] bg-gray-100 overflow-hidden">
                    <img 
                      src={review.image}
                      alt={review.title}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>

                  {/* Review Content */}
                  <div className="p-6">
                    {/* Category Badge */}
                    <span className="inline-block px-3 py-1 bg-[#1A4D4D]/10 text-[#1A4D4D] text-xs font-semibold uppercase rounded-full mb-3">
                      {review.category}
                    </span>

                    {/* Title */}
                    <h3 className="text-[#1A4D4D] text-xl font-bold mb-3 line-clamp-2 group-hover:text-[#FF6B6B] transition-colors">
                      {review.title}
                    </h3>

                    {/* Rating */}
                    <div className="flex items-center gap-2 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <i 
                          key={i}
                          className={`${i < review.rating ? 'ri-star-fill text-[#FFB800]' : 'ri-star-line text-gray-300'} text-lg`}
                        ></i>
                      ))}
                      <span className="text-[#1A4D4D] font-bold text-base ml-1">{review.rating}</span>
                    </div>

                    {/* Meta Info */}
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                      <span>{review.author}</span>
                      <span>•</span>
                      <span>{review.date}</span>
                    </div>

                    {/* Excerpt */}
                    <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                      {review.reviewText.substring(0, 150)}...
                    </p>

                    {/* Read More Link */}
                    <div className="flex items-center gap-2 text-[#FF6B6B] font-semibold text-sm group-hover:gap-3 transition-all">
                      <span>Read Full Review</span>
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-[#1A4D4D] text-2xl font-bold mb-2">No reviews found</h3>
              <p className="text-gray-600">Try selecting a different category</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[#1A4D4D]">
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <pattern id="world-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <circle cx="50" cy="50" r="2" fill="white" opacity="0.3"/>
                <circle cx="20" cy="30" r="1.5" fill="white" opacity="0.2"/>
                <circle cx="80" cy="70" r="1.5" fill="white" opacity="0.2"/>
              </pattern>
              <rect width="100%" height="100%" fill="url(#world-pattern)"/>
            </svg>
          </div>
        </div>

        <div className="relative z-10 max-w-2xl mx-auto text-center px-4 md:px-8">
          <h2 className="text-white text-3xl md:text-4xl lg:text-5xl font-light mb-3 md:mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Never miss a cozy idea
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-6 md:mb-8">
            Weekly decor tips & exclusive deals
          </p>
          
          <form onSubmit={handleNewsletterSubmit} className="flex items-center justify-center gap-0 max-w-sm mx-auto" data-readdy-form id="newsletter-reviews">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              required
              className="flex-1 h-[50px] md:h-[54px] px-4 md:px-5 bg-transparent border-2 border-white/40 rounded-l-full text-white placeholder-white/60 focus:outline-none focus:border-white/60 text-sm"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-[50px] h-[50px] md:w-[54px] md:h-[54px] bg-[#1A4D4D] rounded-r-full flex items-center justify-center text-white hover:bg-[#0F3333] transition-all disabled:opacity-50 whitespace-nowrap"
            >
              <i className="ri-notification-line text-lg md:text-xl"></i>
            </button>
          </form>
          {submitMessage && (
            <p className={`mt-4 text-sm ${submitMessage.includes('Thank') ? 'text-white' : 'text-[#FF6B6B]'}`}>
              {submitMessage}
            </p>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-[#1A4D4D] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <pattern id="home-pattern" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M40 20 L50 30 L50 45 L30 45 L30 30 Z" stroke="white" fill="none" strokeWidth="1"/>
              <circle cx="15" cy="60" r="3" stroke="white" fill="none" strokeWidth="1"/>
              <rect x="60" y="55" width="8" height="8" stroke="white" fill="none" strokeWidth="1"/>
            </pattern>
            <rect width="100%" height="100%" fill="url(#home-pattern)"/>
          </svg>
        </div>

        <div className="relative z-10 px-4 md:px-8 lg:px-20 py-12 md:py-20">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-10 gap-8 md:gap-10">
            <div className="lg:col-span-3">
              <div className="flex items-center gap-2 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/9e46e3d9-adac-48a0-871c-356cdcae054d.png" alt="Simple Home Decor" className="h-8 w-auto brightness-0 invert" />
              </div>
              
              <p className="text-white text-2xl md:text-3xl font-light leading-tight mb-8" style={{ fontFamily: 'Georgia, serif' }}>
                Simple touches.<br />
                Cozy homes.
              </p>
            </div>

            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Shop</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Living Room</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Bedroom</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Kitchen</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Learn</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Blog</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Reviews</Link></li>
                  <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Guides</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Company</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/about" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">About</Link></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Contact</a></li>
                  <li><Link to="/affiliate-disclosure" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Affiliate Disclosure</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Connect</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Pinterest</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Instagram</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Newsletter</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 border-t border-white/20 px-4 md:px-8 lg:px-20 py-8 md:py-12">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-white/80 text-xs md:text-sm text-center md:text-left">
              © 2026 Simple Home Decor. All rights reserved.
            </p>

            <div className="flex items-center gap-3 md:gap-4">
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-pinterest-line text-base md:text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-instagram-line text-base md:text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-facebook-line text-base md:text-lg"></i>
              </a>
              <a href="https://www.youtube.com/@InteriorDecorSolutions" target="_blank" rel="noopener noreferrer" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-youtube-line text-base md:text-lg"></i>
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4 text-white/80 text-xs md:text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors whitespace-nowrap">Privacy Policy</Link>
              <span className="hidden md:inline">|</span>
              <Link to="/terms" className="hover:text-white transition-colors whitespace-nowrap">Terms of Use</Link>
              <span className="hidden md:inline">|</span>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors whitespace-nowrap">Web Design</a>
            </div>
          </div>
          
          <div className="max-w-7xl mx-auto mt-6 pt-6 border-t border-white/10">
            <p className="text-white/60 text-xs text-center leading-relaxed">
              Affiliate Disclosure: This website contains affiliate links. We may earn a commission when you make a purchase through these links at no additional cost to you.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
